import {auth} from '../firebase';

// ─── Single source of truth for backend access ──────────────────────────────
//
// Every backend HTTP call in the app routes through `request()` below. This
// collapses the ~9 near-identical request wrappers that previously lived in
// each service (patientService._templateRequest, onboardingService.onboarding-
// Request, adminService.requestAdminRoute, ehrService.ehrGet, the
// DeveloperError-style fetchers in components/api/*, ...) into one chokepoint
// with exactly one base-URL definition and one token-attach path.
//
// The de-facto contract was already established by patientService:
//   - attach the Firebase ID token as a Bearer header
//   - set Content-Type: application/json when there's a body
//   - parse JSON and throw on `!res.ok || payload.success === false`
//   - return `payload.data`
// `request()` generalizes that to absorb the handful of variants the other
// services needed (raw/text responses, query params, AbortSignal, passthrough
// statuses, full-payload returns, alternate base URLs).

export const BACKEND_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || 'https://api.opalitehealth.com';

// Upper bound on how long any backend call may stay in flight before we abort
// it and surface a normal error. Without this, a stalled connection (laptop
// sleep/wake, Wi-Fi switch, VPN flap, a backgrounded tab whose socket went
// half-open) leaves `fetch` pending *forever* — it never resolves and never
// rejects — so the caller's loading state never clears and the only escape is a
// full page reload. 45s clears normal API calls while still rescuing a hung
// one — but NOT LLM-heavy calls: full-note generation on long visits
// legitimately runs 45–60s+, so scribeService passes its own `timeoutMs` (this
// default silently aborting note generation is exactly the 2026-07-02 "my note
// vanished" incident). Callers that legitimately run longer (LLM note work,
// streaming, large exports, full EHR syncs) pass a larger `timeoutMs`, or 0
// with a caller-side AbortController bound.
export const DEFAULT_REQUEST_TIMEOUT_MS = 45_000;

// Firebase Cloud Functions base (emailEngineService). Separate origin from the
// main backend, kept here so the token-attach path stays unified.
export const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-calliope-e2d62.cloudfunctions.net';

/**
 * Normalized error thrown by `request()`. Carries the HTTP status and the
 * parsed payload (when available) so callers can branch on `err.status` and
 * re-map to their own domain error types (DeveloperError, EncounterCallError, …).
 */
export class ApiError extends Error {
  status: number | null;
  payload: any;

  constructor(
    message: string,
    {
      status = null,
      payload = null,
      cause = null,
    }: {status?: number | null; payload?: any; cause?: unknown} = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
    if (cause) this.cause = cause;
  }
}

/**
 * The ONE token-attach path. Returns a fresh Firebase ID token for the signed-in
 * user, or throws if nobody is signed in. `components/common/firebase.js`
 * re-exports this as `getFirebaseToken` for back-compat.
 */
export async function getAuthToken(forceRefresh = false): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Not authenticated');
  }
  return user.getIdToken(forceRefresh);
}

function buildQueryString(
  query: Record<string, unknown> | string | null | undefined,
): string {
  if (!query) return '';
  if (typeof query === 'string')
    return query.startsWith('?') || query === '' ? query : `?${query}`;
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

/**
 * Make an authenticated backend request.
 *
 * @param path                 Path appended to `baseUrl` (e.g. '/api/v1/...').
 * @param [opts]
 * @param [opts.baseUrl]        Origin to hit (defaults to BACKEND_BASE_URL).
 * @param [opts.query]   Query params; object values are stringified,
 *                                        empty/null/undefined dropped.
 * @param [opts.unwrap=true]   When parse==='json', return `payload.data`
 *                                        instead of the full payload.
 * @param [opts.parse='json']
 *                                        'raw' returns the Response untouched (caller
 *                                        reads the body); 'text' returns response text.
 * @param [opts.auth=true]     Attach the Firebase token. When false, no token
 *                                        is fetched (use `authToken` to supply one).
 * @param [opts.authToken]      Pre-fetched token to use instead of fetching one.
 * @param [opts.successCheck=true]
 *                                        For JSON responses, also treat
 *                                        `payload.success === false` as an error.
 * @param [opts.passthroughStatuses]
 *                                        Statuses returned as `{ __passthroughStatus, ...body }`
 *                                        instead of throwing.
 * @param [opts.signal]    Abort signal forwarded to fetch.
 * @param [opts.timeoutMs]      Abort the request after this many ms (defaults to
 *                                        DEFAULT_REQUEST_TIMEOUT_MS). Pass 0/null to
 *                                        disable for legitimately long calls.
 * @param [opts.headers]        Extra headers (merged; caller can override Content-Type).
 * @param [opts.dedupe=true]    Share the in-flight promise between identical
 *                                        concurrent GETs (same URL + auth identity).
 * @param [opts.freshForMs=5000]
 *                                        Serve an identical GET's result from the
 *                                        short-lived read cache within this window.
 *                                        Pass 0 for endpoints that poll faster than
 *                                        the window (live statuses/transcripts).
 * @throws {ApiError}
 */
export interface RequestOptions extends Omit<RequestInit, 'headers'> {
  baseUrl?: string;
  query?: Record<string, unknown> | string | null;
  unwrap?: boolean;
  parse?: 'json' | 'text' | 'raw';
  auth?: boolean;
  authToken?: string;
  successCheck?: boolean;
  passthroughStatuses?: number[];
  timeoutMs?: number | null;
  headers?: Record<string, string>;
  dedupe?: boolean;
  freshForMs?: number;
}

async function executeRequest(
  path: string,
  {
    baseUrl = BACKEND_BASE_URL,
    query,
    unwrap = true,
    parse = 'json',
    auth: useAuth = true,
    authToken,
    successCheck = true,
    passthroughStatuses,
    signal,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    headers: extraHeaders,
    ...options
  }: RequestOptions = {},
): Promise<any> {
  const url = `${baseUrl}${path}${buildQueryString(query)}`;

  // ── Rejected-token refresh-and-retry ──
  // `getIdToken()` returns a *cached* Firebase ID token; the token lives 60
  // minutes and only auto-refreshes when the SDK notices it has expired. A
  // session that crosses that boundary (a tab left open, a laptop woken from
  // sleep) can send an aged-out token that the backend rejects with 401/403.
  // Without a retry that surfaces as a hard failure — this is exactly the
  // "EHR is not configured" red herring seen when a ~64-minute session's token
  // had expired. When WE own the token (auth on, no caller-supplied token,
  // non-raw parse, no passthrough), force a fresh token and retry ONCE. A
  // 401/403 means the request was rejected, not applied, so retrying is safe
  // even for writes.
  const canAuthRetry =
    useAuth && !authToken && parse !== 'raw' && !passthroughStatuses;
  let forceFreshToken = false;

  for (let attempt = 0; ; attempt += 1) {
    const headers: Record<string, string> = {...(extraHeaders || {})};

    // ── The one token-attach path ──
    let token = authToken;
    if (useAuth && !token) {
      token = await getAuthToken(forceFreshToken);
    }
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Default Content-Type to application/json only for string bodies (the
    // JSON.stringify(...) callers). FormData / Blob / URLSearchParams bodies
    // are left untouched so the browser can set the correct multipart boundary.
    if (
      typeof options.body === 'string' &&
      !headers['Content-Type'] &&
      !headers['content-type']
    ) {
      headers['Content-Type'] = 'application/json';
    }

    // ── Timeout guard (per attempt) ──
    // Compose an internal AbortController so a stalled connection can't pin the
    // request (and the caller's loading spinner) open indefinitely. We honour
    // any caller-supplied `signal` by aborting our controller when it fires.
    const useTimeout =
      Boolean(timeoutMs) && typeof AbortController !== 'undefined';
    const timeoutController = useTimeout ? new AbortController() : null;
    let timedOut = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    if (timeoutController) {
      timer = setTimeout(() => {
        timedOut = true;
        timeoutController.abort();
      }, timeoutMs as number);
      if (signal) {
        if (signal.aborted) timeoutController.abort();
        else
          signal.addEventListener('abort', () => timeoutController.abort(), {
            once: true,
          });
      }
    }
    const effectiveSignal = timeoutController
      ? timeoutController.signal
      : signal;
    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    let res: Response;
    try {
      res = await fetch(url, {...options, headers, signal: effectiveSignal});
    } catch (err) {
      clearTimer();
      if (timedOut) {
        throw new ApiError(`Request timed out after ${timeoutMs}ms`, {
          status: null,
          payload: null,
          cause: err,
        });
      }
      throw err;
    }

    try {
      // Aged-out / rejected token: refresh once and retry before surfacing it.
      if (
        canAuthRetry &&
        attempt === 0 &&
        (res.status === 401 || res.status === 403)
      ) {
        forceFreshToken = true;
        continue;
      }

      if (passthroughStatuses && passthroughStatuses.includes(res.status)) {
        const body = await res.json().catch(() => ({}));
        return {__passthroughStatus: res.status, ...body};
      }

      if (parse === 'raw') {
        // The caller takes full ownership of the Response — status checks and
        // body parsing are theirs. We never throw here (used by ehrService's
        // acceptedStatuses handling and by streaming/chat-completion callers).
        // The `finally` clears our timer immediately so we never abort a valid,
        // possibly long-lived, body stream the caller is still reading.
        return res;
      }

      if (parse === 'text') {
        const text = await res.text().catch(() => '');
        if (!res.ok) {
          throw new ApiError(`Request failed with status ${res.status}`, {
            status: res.status,
            payload: text,
          });
        }
        return text;
      }

      // parse === 'json'
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || (successCheck && payload.success === false)) {
        throw new ApiError(
          payload.error || `Request failed with status ${res.status}`,
          {
            status: res.status,
            payload,
          },
        );
      }
      return unwrap ? payload.data : payload;
    } finally {
      clearTimer();
    }
  }
}

// ─── GET dedupe + short read cache ───────────────────────────────────────────
//
// Post-Schema-V2, every read is an HTTP call through this chokepoint, and the
// UI routinely asks for the same resource more than once within a second or
// two (badge + page loading the same queue, detail surfaces re-reading an
// encounter a sibling loader just fetched, SWR remount revalidation). Rather
// than chase each duplicate at its call site, identical GETs share one network
// round-trip here:
//   - in-flight dedupe: concurrent identical GETs await the same fetch;
//   - freshness window: a completed GET's result is replayed for
//     DEFAULT_GET_FRESH_MS (callers that poll faster pass freshForMs: 0);
//   - write-through invalidation: ANY non-GET clears the read cache, so
//     write-then-refetch flows always observe their own writes.
// Cached values are cloned on the way in and out — callers can mutate what
// they receive without poisoning later hits. Raw-Response, caller-signal,
// explicit-token and passthrough requests are excluded (lifecycle-specific).

export const DEFAULT_GET_FRESH_MS = 5_000;
const GET_CACHE_LIMIT = 100;
const getCache = new Map<string, {value: unknown; storedAt: number}>();
const getInFlight = new Map<string, Promise<unknown>>();

export function clearRequestReadCache(): void {
  getCache.clear();
}

function cloneResult<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value;
  try {
    if (typeof structuredClone === 'function') return structuredClone(value);
  } catch {
    /* non-cloneable — fall through to the JSON path */
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

export async function request(
  path: string,
  opts: RequestOptions = {},
): Promise<any> {
  const {dedupe = true, freshForMs = DEFAULT_GET_FRESH_MS, ...rest} = opts;
  const method = String(rest.method || 'GET').toUpperCase();

  if (method !== 'GET') {
    getCache.clear();
    return executeRequest(path, rest);
  }

  const eligible =
    rest.parse !== 'raw' &&
    !rest.signal &&
    !rest.authToken &&
    !rest.passthroughStatuses &&
    (dedupe || freshForMs > 0);
  if (!eligible) return executeRequest(path, rest);

  const key = [
    `${rest.baseUrl ?? BACKEND_BASE_URL}${path}${buildQueryString(rest.query)}`,
    rest.auth === false ? 'noauth' : auth.currentUser?.uid || '',
    rest.parse ?? 'json',
    rest.unwrap === false ? 'full' : 'data',
    rest.successCheck === false ? 'lax' : 'strict',
  ].join('§');

  if (freshForMs > 0) {
    const hit = getCache.get(key);
    if (hit && Date.now() - hit.storedAt < freshForMs) {
      return cloneResult(hit.value);
    }
  }
  if (dedupe) {
    const pending = getInFlight.get(key);
    if (pending) return pending.then(cloneResult);
  }

  const flight = executeRequest(path, rest);
  if (dedupe) getInFlight.set(key, flight);
  try {
    const value = await flight;
    getCache.set(key, {value: cloneResult(value), storedAt: Date.now()});
    while (getCache.size > GET_CACHE_LIMIT) {
      const oldest = getCache.keys().next().value;
      if (!oldest) break;
      getCache.delete(oldest);
    }
    return value;
  } finally {
    if (dedupe) getInFlight.delete(key);
  }
}

const apiClient = {
  request,
  getAuthToken,
  ApiError,
  BACKEND_BASE_URL,
  FUNCTIONS_BASE_URL,
};
export default apiClient;
